import { keepPreviousData, useQuery } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { readComplianceBody } from "../helper/readComplianceBody";
import type {
  DuplicateIdentityGroupsListResponse,
  DuplicateIdentityStatus,
} from "./types";

type StoredToken = { AccessToken?: string };

export const GET_DUPLICATE_IDENTITY_GROUPS_QUERY_KEY =
  "GET_DUPLICATE_IDENTITY_GROUPS";

export type DuplicateIdentityGroupsFilters = {
  // Opaque cursor token from a previous response's `next_page`/`previous_page`
  // (admin-panel-v2's `fastapi-pagination` `CursorPage`) — `null`/undefined
  // means "first page". There is no page-number/offset access.
  cursor?: string | null;
  size?: number;
  tenant?: string;
  status?: DuplicateIdentityStatus;
  statuses?: DuplicateIdentityStatus[];
};

const EMPTY_LIST: DuplicateIdentityGroupsListResponse = {
  items: [],
  next_page: null,
};

export async function fetchDuplicateIdentityGroups(
  filters: DuplicateIdentityGroupsFilters,
): Promise<DuplicateIdentityGroupsListResponse> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken,
  );

  const params: Record<string, unknown> = {
    size: filters.size ?? 20,
  };

  if (filters.cursor) {
    params.cursor = filters.cursor;
  }

  if (filters.tenant?.trim()) {
    params.tenant = filters.tenant.trim();
  }

  if (filters.status) {
    params.status = filters.status;
  } else if (filters.statuses?.length) {
    // fastapi-filters accepts a bare comma-separated value for the default
    // ("in") operator on this field — see
    // `contexts/compliance/duplicate_identity/schemas/filters.py`.
    params.statuses = filters.statuses.join(",");
  }

  const response = await PrivateAdminPanelInstance.get(
    API_ROUTES.GET_DUPLICATE_IDENTITY_GROUPS,
    { params },
  );

  const raw = readComplianceBody<DuplicateIdentityGroupsListResponse>(response);
  if (!raw || !Array.isArray(raw.items)) {
    return { ...EMPTY_LIST };
  }

  return raw;
}

export const useGetDuplicateIdentityGroups = (
  filters: DuplicateIdentityGroupsFilters = {},
) => {
  return useQuery({
    queryKey: [GET_DUPLICATE_IDENTITY_GROUPS_QUERY_KEY, filters],
    queryFn: () => fetchDuplicateIdentityGroups(filters),
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
};
